import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import "./styles/CompareSitesViewer.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function CompareSitesViewer() {
    const [masterSites, setMasterSites] = useState([]);
    const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem("token");
        const authHeader = {
            headers: { Authorization: `Bearer ${token}` },
        };

        axios
            .get("/api/mastersites", authHeader)
            .then((res) => {
                setMasterSites(res.data);
                if (res.data.length > 0) {
                    setSelectedMasterSiteId(res.data[0]._id);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch MasterSites", err);
                alert("❌ Failed to load Master Sites");
            });
    }, []);

    const fetchComparisonData = async () => {
        if (!selectedMasterSiteId || !fromDate || !toDate) {
            alert("⚠️ Please select Master Site and both dates.");
            return;
        }

        if (new Date(fromDate) > new Date(toDate)) {
            alert("⚠️ 'From' date cannot be after 'To' date.");
            return;
        }

        setLoading(true);
        setChartData(null);

        try {
            const formatDate = (isoDate) => {
                const d = new Date(isoDate);
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                return `${day}/${month}/${year}`;
            };

            const fromFormatted = formatDate(fromDate);
            const toFormatted = formatDate(toDate);
            let url = "";
            //const url = "/api/reports/compare/master/${selectedMasterSiteId}?from=${fromFormatted}&to=${toFormatted}";
            console.log("DEBUG ->", {
                selectedMasterSiteId,
                fromFormatted,
                toFormatted,
                url: "/api/graphtotal/compare/master/" + selectedMasterSiteId + "?from=" + fromFormatted + "&to=" + toFormatted
            });

            url = `/api/graphtotal/compare/master/${selectedMasterSiteId}?from=${fromFormatted}&to=${toFormatted}`;
            console.log("Fetching comparison data from:", url);
            const token = localStorage.getItem("token");
            const authHeader = {
                headers: { Authorization: `Bearer ${token}` },
            };

            console.log("Auth Header:", authHeader);
            console.log("📡 Fetching comparison data from:", url);
            const res = await axios.get(url, authHeader);
            if (res.data.length === 0) {
                alert("ℹ️ No data available for selected date range.");
                setChartData(null);
                return;
            }

            const labels = res.data.map((r) => r.site);
            const water = res.data.map((r) =>
                parseFloat(r.totalWaterDispensed?.$numberDecimal || r.totalWaterDispensed || 0)
            );
            const amount = res.data.map((r) =>
                parseFloat(r.totalAmountCollected?.$numberDecimal || r.totalAmountCollected || 0)
            );

            setChartData({
                labels,
                datasets: [
                    {
                        label: "Water Dispensed (L)",
                        data: water,
                        backgroundColor: "rgba(54, 162, 235, 0.7)",
                    },
                    {
                        label: "Amount Collected (₹)",
                        data: amount,
                        backgroundColor: "rgba(255, 99, 132, 0.7)",
                    },
                ],
            });

            alert(`✅ Data fetched for ${labels.length} sites.`);
        } catch (err) {
            console.error("Comparison data fetch failed:", err);
            alert("❌ Failed to fetch comparison data. Check backend logs.");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="compare-sites-container">
            <h2>Compare Water Usage & Revenue by Site</h2>

            <div className="compare-filters">
                <select
                    className="input-control"
                    value={selectedMasterSiteId}
                    onChange={(e) => setSelectedMasterSiteId(e.target.value)}
                >
                    {masterSites.map((site) => (
                        <option key={site._id} value={site._id}>
                            {site.name}
                        </option>
                    ))}
                </select>

                <input
                    type="date"
                    className="input-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                />
                <input
                    type="date"
                    className="input-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                />

                <button className="btn fetch" onClick={fetchComparisonData}>
                    Fetch Comparison
                </button>
            </div>

            {loading ? (
                <p className="loading">⏳ Loading data...</p>
            ) : chartData ? (
                <div><div className="chart-box">
                    <h3>Total Water Dispensed (Litres) for each Site in the date range</h3>
                    <Bar
                        data={{
                            labels: chartData.labels,
                            datasets: [
                                {
                                    label: "Water Dispensed (L)",
                                    data: chartData.datasets[0].data,
                                    backgroundColor: "rgba(54, 162, 235, 0.7)",
                                },
                            ],
                        }}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: { position: "top" },
                                title: {
                                    display: true,
                                    text: "Water Dispensed by Site",
                                },
                            },
                        }}
                    />
                </div>

                    <div className="chart-box">
                        <h3>TotalAmount Collected (₹) for each Site in the date range</h3>
                        <Bar
                            data={{
                                labels: chartData.labels,
                                datasets: [
                                    {
                                        label: "Amount Collected (₹)",
                                        data: chartData.datasets[1].data,
                                        backgroundColor: "rgba(255, 99, 132, 0.7)",
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                plugins: {
                                    legend: { position: "top" },
                                    title: {
                                        display: true,
                                        text: "Amount Collected by Site",
                                    },
                                },
                            }}
                        />
                    </div>
                </div>

            ) : (
                <p className="no-data">No data available. Select filters and click fetch.</p>
            )}
        </div>
    );
}
